import axios from 'axios';
import { createAsyncThunk, createSlice, isPending, isRejected } from '@reduxjs/toolkit';
import { API_ROUTES } from '@/config/api-routes';
import { AIRPLANE_TICKET_STATUS_TABS, } from '@/shared/model/airplane-ticket.model';
import { buildFinalApprovalFormData, buildInvoiceFormData, buildOfferFormData, buildRescheduleFormData, readDownloadFilename, } from '@/shared/utils/airplane-ticket-api';
import { appendPageable, readPagedRows } from '@/shared/utils/spring-api';
import { serializeAxiosError, resolveAsyncThunkErrorMessage } from './reducer.utils';
const api = API_ROUTES.airplaneTickets;
const initialState = {
    loading: false,
    mutationLoading: false,
    errorMessage: null,
    bookings: [],
    totalBookings: 0,
    tabCounts: {},
    selectedBooking: null,
};
function listQuery(params) {
    const q = new URLSearchParams();
    q.set('status', params.status);
    appendPageable(q, params.page, params.size, params.sort ?? 'createdDate,desc');
    if (params.search?.trim()) {
        q.set('search', params.search.trim());
    }
    return `${api.base}?${q.toString()}`;
}
export const fetchAirplaneTickets = createAsyncThunk('airplaneTicket/fetch_list', async (params) => {
    const response = await axios.get(listQuery(params));
    const { rows, total } = readPagedRows(response.data, response.headers);
    return { rows, total, status: params.status };
}, { serializeError: serializeAxiosError });
export const fetchAirplaneTicketTabCounts = createAsyncThunk('airplaneTicket/fetch_tab_counts', async (search) => {
    const counts = {};
    await Promise.all(AIRPLANE_TICKET_STATUS_TABS.map(async (status) => {
        const q = new URLSearchParams();
        q.set('status', status);
        appendPageable(q, 0, 1, 'createdDate,desc');
        if (search?.trim())
            q.set('search', search.trim());
        const response = await axios.get(`${api.base}?${q.toString()}`);
        const { total } = readPagedRows(response.data, response.headers);
        counts[status] = total;
    }));
    return counts;
}, { serializeError: serializeAxiosError });
export const fetchAirplaneTicketById = createAsyncThunk('airplaneTicket/fetch_one', async (id) => axios.get(api.one(id)), { serializeError: serializeAxiosError });
export const createAirplaneTicketOffer = createAsyncThunk('airplaneTicket/create_offer', async ({ id, offer, quotationFile, }) => axios.post(api.offers(id), buildOfferFormData(offer, quotationFile), {
    headers: { 'Content-Type': 'multipart/form-data' },
}), { serializeError: serializeAxiosError });
export const uploadAirplaneTicketInvoice = createAsyncThunk('airplaneTicket/upload_invoice', async ({ id, invoiceFile }) => axios.post(api.invoices(id), buildInvoiceFormData(invoiceFile), {
    headers: { 'Content-Type': 'multipart/form-data' },
}), { serializeError: serializeAxiosError });
export const finalApproveAirplaneTicket = createAsyncThunk('airplaneTicket/final_approval', async ({ id, approval, ticketFile, }) => axios.post(api.finalApproval(id), buildFinalApprovalFormData(approval, ticketFile), { headers: { 'Content-Type': 'multipart/form-data' } }), { serializeError: serializeAxiosError });
export const approveAirplaneTicketCancellation = createAsyncThunk('airplaneTicket/approve_cancellation', async ({ id, body }) => axios.post(api.approveCancellation(id), body), { serializeError: serializeAxiosError });
export const rescheduleAirplaneTicket = createAsyncThunk('airplaneTicket/reschedule', async ({ id, reschedule, invoiceFile, }) => axios.post(api.reschedule(id), buildRescheduleFormData(reschedule, invoiceFile), { headers: { 'Content-Type': 'multipart/form-data' } }), { serializeError: serializeAxiosError });
export const rejectAirplaneTicketReschedule = createAsyncThunk('airplaneTicket/reject_reschedule', async ({ id, body }) => axios.post(api.rejectReschedule(id), body), { serializeError: serializeAxiosError });
export const rejectAirplaneTicketIssuance = createAsyncThunk('airplaneTicket/reject_issuance', async ({ id, body }) => axios.post(api.rejectIssuance(id), body), { serializeError: serializeAxiosError });
export const rejectAirplaneTicketCancellation = createAsyncThunk('airplaneTicket/reject_cancellation', async ({ id, body }) => axios.post(api.rejectCancellation(id), body), { serializeError: serializeAxiosError });
export const downloadAirplaneTicketFile = createAsyncThunk('airplaneTicket/download_file', async ({ id, fileType, fallbackName }) => {
    const response = await axios.get(api.file(id, fileType), { responseType: 'blob' });
    return { blob: response.data, filename: readDownloadFilename(response, fallbackName) };
}, { serializeError: serializeAxiosError });
const mutationThunks = [
    createAirplaneTicketOffer,
    uploadAirplaneTicketInvoice,
    finalApproveAirplaneTicket,
    approveAirplaneTicketCancellation,
    rescheduleAirplaneTicket,
    rejectAirplaneTicketReschedule,
    rejectAirplaneTicketIssuance,
    rejectAirplaneTicketCancellation,
];
export const airplaneTicketSlice = createSlice({
    name: 'airplaneTicket',
    initialState: initialState,
    reducers: {
        clearAirplaneTicketError(state) {
            state.errorMessage = null;
        },
        setSelectedAirplaneTicket(state, action) {
            state.selectedBooking = action.payload;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(fetchAirplaneTickets.fulfilled, (state, action) => {
            state.loading = false;
            state.bookings = action.payload.rows;
            state.totalBookings = action.payload.total;
            state.tabCounts[action.payload.status] = action.payload.total;
        })
            .addCase(fetchAirplaneTicketTabCounts.fulfilled, (state, action) => {
            state.tabCounts = { ...state.tabCounts, ...action.payload };
        })
            .addCase(fetchAirplaneTicketById.fulfilled, (state, action) => {
            state.selectedBooking = action.payload.data;
        })
            .addMatcher(isPending(fetchAirplaneTickets), (state) => {
            state.loading = true;
            state.errorMessage = null;
        })
            .addMatcher(isPending(...mutationThunks), (state) => {
            state.mutationLoading = true;
            state.errorMessage = null;
        })
            .addMatcher(isRejected(fetchAirplaneTickets), (state, action) => {
            state.loading = false;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        })
            .addMatcher(isRejected(...mutationThunks), (state, action) => {
            state.mutationLoading = false;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        })
            .addMatcher(isPending(fetchAirplaneTicketTabCounts), () => { })
            .addMatcher((action) => mutationThunks.some((t) => t.fulfilled.match(action)), (state) => {
            state.mutationLoading = false;
        });
    },
});
export const { clearAirplaneTicketError, setSelectedAirplaneTicket } = airplaneTicketSlice.actions;
export default airplaneTicketSlice.reducer;
